/**
 * <vehicle-garage> — the header "My Garage" button + modal.
 *
 * The button shows the shopper's ACTIVE car (or a default label when the garage is empty) and
 * opens a modal with two modes:
 *   - List mode: the saved cars (tap one to make it active + shop its collection; remove with the
 *     trailing x) plus an "Add a vehicle" button.
 *   - Finder mode: the shared <parts-finder> cascade. Picking a model adds the car to the garage,
 *     makes it active, and routes to its collection.
 *
 * Data lives in the shared garage-store (device-local localStorage). This element owns the button
 * state, the modal chrome (open/close, focus, scroll lock, portal), and rendering the car list;
 * the store owns persistence + the `vehicle:change` event (the Algolia fitment hook).
 */

import { getList, getActive, setActive, removeVehicle } from '@theme/garage-store';

const FOCUSABLE = 'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

class VehicleGarage extends HTMLElement {
  /** @type {HTMLButtonElement | null} */ #trigger = null;
  /** @type {HTMLElement | null} */ #label = null;
  /** @type {HTMLElement | null} */ #modal = null;
  /** @type {HTMLElement | null} */ #backdrop = null;
  /** @type {HTMLButtonElement | null} */ #close = null;
  /** @type {HTMLButtonElement | null} */ #back = null;
  /** @type {HTMLElement | null} */ #titleText = null;
  /** @type {HTMLElement | null} */ #titleIcon = null;
  /** @type {HTMLElement | null} */ #listView = null;
  /** @type {HTMLElement | null} */ #finderView = null;
  /** @type {HTMLUListElement | null} */ #list = null;
  /** @type {HTMLButtonElement | null} */ #add = null;
  /** @type {HTMLButtonElement | null} */ #selectSubmit = null;
  /** @type {string | null} */ #selectedUrl = null;
  /** @type {Element | null} */ #lastFocus = null;
  #defaultLabel = '';
  #open = false;
  #portaled = false;

  connectedCallback() {
    this.#defaultLabel = this.getAttribute('data-default-label') || '';
    this.#trigger = this.querySelector('.vehicle-garage__trigger');
    this.#label = this.querySelector('.vehicle-garage__label');
    this.#modal = this.querySelector('.vehicle-garage__modal');
    this.#backdrop = this.querySelector('.vehicle-garage__backdrop');
    this.#close = this.querySelector('.vehicle-garage__close');
    this.#back = this.querySelector('.vehicle-garage__back');
    this.#titleText = this.querySelector('.vehicle-garage__modal-title-text');
    this.#titleIcon = this.querySelector('.vehicle-garage__title-icon');
    this.#listView = this.querySelector('.vehicle-garage__list-view');
    this.#finderView = this.querySelector('.vehicle-garage__finder-view');
    this.#list = this.querySelector('.vehicle-garage__list');
    this.#add = this.querySelector('.vehicle-garage__add');
    this.#selectSubmit = this.querySelector('.vehicle-garage__select-submit');

    if (!this.#trigger || !this.#modal) return;

    this.#renderTrigger();

    this.#trigger.addEventListener('click', this.#toggle);
    this.#close?.addEventListener('click', this.#closeModal);
    this.#backdrop?.addEventListener('click', this.#closeModal);
    this.#back?.addEventListener('click', this.#showList);
    this.#add?.addEventListener('click', this.#showFinder);
    this.#selectSubmit?.addEventListener('click', this.#selectActive);
    this.#list?.addEventListener('click', this.#handleListClick);
    // Keydown is bound to the modal (not the host) because the modal is portaled out to <body>.
    this.#modal.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('vehicle:change', this.#handleVehicleChange);
  }

  disconnectedCallback() {
    document.removeEventListener('vehicle:change', this.#handleVehicleChange);
    if (this.#open) this.#unlockScroll();
    if (this.#portaled) {
      this.#backdrop?.remove();
      this.#modal?.remove();
    }
  }

  // ── Trigger / button state ────────────────────────────────────────────────────────────────
  #renderTrigger() {
    const active = getActive();
    if (active && active.label && this.#label) {
      this.#label.textContent = active.label;
      this.dataset.hasVehicle = 'true';
    } else if (this.#label) {
      this.#label.textContent = this.#defaultLabel;
      delete this.dataset.hasVehicle;
    }
  }

  // ── Open / close ──────────────────────────────────────────────────────────────────────────
  #toggle = (event) => {
    event.preventDefault();
    if (this.#open) this.#closeModal();
    else this.#openModal();
  };

  #openModal = () => {
    if (!this.#modal || this.#open) return;
    this.#open = true;
    this.#lastFocus = document.activeElement;
    // Each open starts the pending selection from the persisted active car (set in #renderList).
    this.#selectedUrl = null;
    this.#portalToBody();
    // Start in list mode if there are saved cars, otherwise straight to the finder.
    if (getList().length > 0) this.#showList();
    else this.#showFinder();
    if (this.#backdrop) this.#backdrop.hidden = false;
    this.#modal.hidden = false;
    this.#trigger?.setAttribute('aria-expanded', 'true');
    this.#lockScroll();
    this.#focusFirst();
  };

  #closeModal = () => {
    if (!this.#modal || !this.#open) return;
    this.#open = false;
    this.#modal.hidden = true;
    if (this.#backdrop) this.#backdrop.hidden = true;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#unlockScroll();
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  };

  // ── View switching ────────────────────────────────────────────────────────────────────────
  #showList = () => {
    this.#renderList();
    if (this.#listView) this.#listView.hidden = false;
    if (this.#finderView) this.#finderView.hidden = true;
    if (this.#back) this.#back.hidden = true;
    if (this.#titleText) this.#titleText.textContent = this.getAttribute('data-title-garage') || '';
    if (this.#titleIcon) this.#titleIcon.hidden = false;
  };

  #showFinder = () => {
    const hasCars = getList().length > 0;
    if (this.#listView) this.#listView.hidden = true;
    if (this.#finderView) this.#finderView.hidden = false;
    // A "back to garage" affordance only makes sense when there are cars to go back to.
    if (this.#back) this.#back.hidden = !hasCars;
    if (this.#titleText) this.#titleText.textContent = this.getAttribute('data-title-add') || '';
    // The car mark belongs to "My Garage"; hide it in the "Add a vehicle" finder view.
    if (this.#titleIcon) this.#titleIcon.hidden = true;
    // The nested finder defers its fetch; load it now (idempotent) when first shown.
    this.#finderView?.querySelector('parts-finder')?.ensureLoaded?.();
  };

  #renderList() {
    if (!this.#list) return;
    const cars = getList();
    const active = getActive();
    // Default / validate the pending selection (what "Select" shops): keep the shopper's tapped
    // choice if it's still in the garage, otherwise fall back to the active car, then the newest.
    if (!this.#selectedUrl || !cars.some((c) => c.url === this.#selectedUrl)) {
      this.#selectedUrl = (active && active.url) || (cars[0] && cars[0].url) || null;
    }
    const removeTpl = this.getAttribute('data-remove-label') || 'Remove %s';
    this.#list.replaceChildren();

    for (const car of cars) {
      const isSelected = car.url === this.#selectedUrl;

      const li = document.createElement('li');
      li.className = 'vehicle-garage__row';
      if (isSelected) li.dataset.active = 'true';

      const pick = document.createElement('button');
      pick.type = 'button';
      pick.className = 'vehicle-garage__pick';
      pick.dataset.url = car.url;
      pick.setAttribute('aria-current', isSelected ? 'true' : 'false');

      const name = document.createElement('span');
      name.className = 'vehicle-garage__pick-label';
      name.textContent = car.label;

      const check = document.createElement('span');
      check.className = 'vehicle-garage__pick-check';
      check.setAttribute('aria-hidden', 'true');
      check.innerHTML =
        '<svg viewBox="0 0 20 20" fill="none"><path d="M5 10.5l3.5 3.5L15 7" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      pick.append(name, check);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'vehicle-garage__remove';
      remove.dataset.url = car.url;
      remove.dataset.action = 'remove';
      remove.setAttribute('aria-label', removeTpl.replace('%s', car.label));
      remove.innerHTML =
        '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" d="m6 6 8 8M14 6l-8 8"/></svg>';

      li.append(pick, remove);
      this.#list.append(li);
    }
  }

  #handleListClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const removeBtn = target.closest('.vehicle-garage__remove');
    if (removeBtn instanceof HTMLElement) {
      event.preventDefault();
      removeVehicle(removeBtn.dataset.url || '');
      // Re-render: if the garage is now empty, drop into the finder; otherwise refresh the list.
      if (getList().length === 0) this.#showFinder();
      else this.#renderList();
      return;
    }

    const pick = target.closest('.vehicle-garage__pick');
    if (pick instanceof HTMLElement) {
      event.preventDefault();
      // Tapping a car highlights it (pending selection); the "Select" button commits + shops it.
      this.#selectedUrl = pick.dataset.url || null;
      this.#renderList();
    }
  };

  // The list's commit action: persist the highlighted car as active (fires `vehicle:change` for the
  // Algolia fitment hook) and route to its collection.
  #selectActive = () => {
    if (!this.#selectedUrl) return;
    setActive(this.#selectedUrl);
    window.location.href = this.#selectedUrl;
  };

  // ── A11y plumbing ─────────────────────────────────────────────────────────────────────────
  #focusFirst() {
    const view = this.#finderView && !this.#finderView.hidden ? this.#finderView : this.#listView;
    const first = view?.querySelector(FOCUSABLE) || this.#close;
    if (first instanceof HTMLElement) first.focus();
  }

  #handleKeydown = (event) => {
    if (!this.#open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeModal();
      return;
    }
    if (event.key === 'Tab') this.#trapFocus(event);
  };

  #trapFocus(event) {
    if (!this.#modal) return;
    const items = [...this.#modal.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = this.getRootNode().activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #handleVehicleChange = () => {
    this.#renderTrigger();
    // Keep an open list in sync (e.g. another tab changed the garage).
    if (this.#open && this.#listView && !this.#listView.hidden) this.#renderList();
  };

  // ── Portal + scroll lock ──────────────────────────────────────────────────────────────────
  /**
   * Move the backdrop + modal to <body> on first open. The header sets `contain: layout`, which
   * makes it the containing block for fixed-positioned descendants — so a modal left inside the
   * header would center on the (short) header and overflow off the top of the screen. Portaling to
   * <body> makes `position: fixed` resolve against the viewport. Done once; nodes stay on body.
   */
  #portalToBody() {
    if (this.#portaled) return;
    this.#portaled = true;
    if (this.#backdrop) document.body.appendChild(this.#backdrop);
    if (this.#modal) document.body.appendChild(this.#modal);
  }

  #lockScroll() {
    this.dataset.prevOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.body.style.overflow = this.dataset.prevOverflow || '';
    delete this.dataset.prevOverflow;
  }
}

if (!customElements.get('vehicle-garage')) {
  customElements.define('vehicle-garage', VehicleGarage);
}
